
import { EmailTemplateType } from './types';

interface TemplateGroup {
  label: string;
  templates: Array<{ value: string; label: string }>;
}

// Template categorization mapping - only for primary templates
const templateCategoryMap: Record<string, string> = {
  // Test templates
  custom: 'Test Email',
  
  // Authentication templates
  authSignup: 'Authentication',
  authLogin: 'Authentication',
  resetPassword: 'Authentication',
  verifyEmail: 'Authentication',
  
  // Account status templates
  accountSuspended: 'Account Status',
  accountReactivated: 'Account Status',
  accountStatus: 'Account Status',
  roleChange: 'Account Status',
  
  // Account deletion templates
  accountDeletionRequest: 'Account Deletion',
  accountDeletionConfirmed: 'Account Deletion',
  accountDeletionCompleted: 'Account Deletion',
  
  // Document verification templates
  documentVerified: 'Document Verification',
  documentRejected: 'Document Verification',
  
  // Polls & insights templates
  newPollPublished: 'Polls & Insights',
  pollClosed: 'Polls & Insights',
  pollsterStatusUpdated: 'Polls & Insights',
  insightUpdated: 'Polls & Insights',
  
  // Waitlist templates
  waitlistLaunch: 'Waitlist',
  waitlistConfirmation: 'Waitlist',
  
  // Contact templates
  contactAutoResponse: 'Contact Support',
  contactAdminNotification: 'Contact Support',
  contactStatusUpdate: 'Contact Support',
};

// Available template types
const availableTemplateTypes: EmailTemplateType[] = [
  'authSignup', 'authLogin', 'documentVerified', 'documentRejected',
  'newPollPublished', 'pollClosed', 'insightUpdated', 'pollsterStatusUpdated',
  'accountDeletionPending', 'accountDeletionConfirmed', 'accountDeletionCompleted',
  'waitlistConfirmation', 'contactAutoResponse', 'contactAdminNotification', 'contactStatusUpdate'
];

// Function to convert template key to human-readable label
const formatTemplateLabel = (key: string): string => {
  // Handle special cases first
  if (key === 'custom') return 'Simple Test Email';
  if (key === 'authSignup') return 'Auth Sign Up';
  if (key === 'authLogin') return 'Auth Login';
  if (key === 'resetPassword') return 'Reset Password';
  if (key === 'verifyEmail') return 'Verify Email';
  if (key === 'accountSuspended') return 'Account Suspended';
  if (key === 'accountReactivated') return 'Account Reactivated';
  if (key === 'accountDeletionRequest') return 'Account Deletion Request';
  if (key === 'accountDeletionConfirmed') return 'Account Deletion Confirmed';
  if (key === 'accountDeletionCompleted') return 'Account Deletion Completed';
  if (key === 'documentVerified') return 'Document Verified';
  if (key === 'documentRejected') return 'Document Rejected';
  if (key === 'newPollPublished') return 'New Poll Published';
  if (key === 'pollClosed') return 'Poll Closed';
  if (key === 'pollsterStatusUpdated') return 'Pollster Status Updated';
  if (key === 'insightUpdated') return 'Insight Updated';
  if (key === 'waitlistLaunch') return 'Waitlist Launch';
  if (key === 'waitlistConfirmation') return 'Waitlist Confirmation';
  if (key === 'contactAutoResponse') return 'Contact Auto Response';
  if (key === 'contactAdminNotification') return 'Contact Admin Notification';
  if (key === 'contactStatusUpdate') return 'Contact Status Update';
  
  // For other cases, convert camelCase to readable format
  return key
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
    .trim();
};

// Function to get category for a template key
const getTemplateCategory = (key: string): string => {
  return templateCategoryMap[key] || 'Other';
};

// Main function to generate template groups dynamically
export const generateTemplateGroups = (): TemplateGroup[] => {
  const categoryMap = new Map<string, Array<{ value: string; label: string }>>();
  
  // Process only the available template types
  availableTemplateTypes.forEach(templateKey => {
    const category = getTemplateCategory(templateKey);
    const label = formatTemplateLabel(templateKey);
    
    if (!categoryMap.has(category)) {
      categoryMap.set(category, []);
    }
    
    categoryMap.get(category)!.push({ value: templateKey, label });
  });
  
  // Convert map to array and sort categories
  const groups: TemplateGroup[] = [];
  const categoryOrder = [
    'Test Email',
    'Authentication', 
    'Account Status',
    'Account Deletion',
    'Document Verification',
    'Polls & Insights',
    'Waitlist',
    'Contact Support',
    'Other'
  ];
  
  categoryOrder.forEach(category => {
    if (categoryMap.has(category)) {
      const templates = categoryMap.get(category)!;
      // Sort templates within each category
      templates.sort((a, b) => a.label.localeCompare(b.label));
      groups.push({ label: category, templates });
    }
  });
  
  return groups;
};
